import { createSelector } from 'reselect';
import InvoiceSettingsApi from './InvoiceSettingsApi';
import { clearServerData, receivedServerData } from '../Common/Data/DataFetcher';
import RequestApi from '../Customers/api/RequestApi';
import { getCustomerContactInfo } from '../Customers/Show/CustomerData';
import {
  getAttachmentsInfo,
  getCurrentSegmentDue,
  getJob,
  getSegmentArray,
  getSegmentOverviewDue,
  getSegments,
  JOB_NAMESPACE,
} from '../../Resources/Job/JobData';
import {
  getCompanyName,
  getCustomerName,
  getParentCustomerDisplayName,
  getInvoiceNumber,
  isEstimate,
  getDescription,
  getStartTime,
  getCompletionTime,
  getSelectedSegmentIds,
} from '../../Resources/Job/JobData';
import {
  getDepositAmount,
  getJobInboxFromJob,
  getPartnerName,
  getRemainingDeposit,
} from '../Job/Details/JobDetailsData';
import numeral from 'numeral';
import {
  getSelectedJobInvoiceNumbers,
  getCustomerSelectedInvoiceNumbers,
  getSelectedTotalOutstandingBalance,
  getCustomerOutstandingBalanceFromSelectedJobs,
} from '../Customers/Show/Requests/Data';
import { formatPhoneNumber } from '../Common/Form/FormValidations';
import moment from 'moment';
import { isPresent } from 'utils/isPresent';

export const INVOICE_OPTION_NAMESPACE = 'invoiceOptions';
export const INVOICE_BODY_NAMESPACE = 'invoiceBody';
export const INVOICE_DIALOG_NAMESPACE = 'invoiceDialog';

export const getInvoiceOptions = createSelector(
  (state) => state[INVOICE_OPTION_NAMESPACE],
  (options) => {
    return options || {};
  }
);

export const getInvoiceBody = createSelector(
  (state) => state.invoiceBody,
  (response) => {
    return (response || {}).body;
  }
);

export const getOrganizationName = createSelector(
  (state) => state.customerListData?.pro_info?.organization_name,
  (name) => {
    return name || {};
  }
);

export const canOrgPayOnline = createSelector(getInvoiceOptions, (options) => {
  return options.has_real_bank_account;
});

export function getCustomEmailSubject(isBatch, isOverview) {
  if (isBatch) {
    return getDefaultCustomEmailSubjectBatch;
  } else if (isOverview) {
    return getDefaultCustomEmailSubjectOverview;
  }
  return getDefaultCustomEmailSubjectSingleJob;
}

const findSegmentByUuid = (segments, uuid) => segments.find((segment) => segment.uuid === uuid);

const getOutstandingBalanceForSegments = (segments, selectedSegmentUuids) => {
  const selectedSegments = selectedSegmentUuids
    .map((uuid) => findSegmentByUuid(segments, uuid))
    .filter((segment) => isPresent(segment));

  return selectedSegments.reduce((acc, segment) => acc + segment.outstanding_balance || 0, 0);
};

export const getDefaultCustomEmailSubjectSingleJob = createSelector(
  [isEstimate, getCompanyName, getDescription, getCurrentSegmentDue, getSelectedSegmentIds, getRemainingDeposit],
  (isEstimate, companyName, description, currentDueInCents, selectedSegmentIds, remainingDeposit) => {
    if (isEstimate) {
      return `Estimate from ${companyName} - ${description}`;
    } else if (selectedSegmentIds.length <= 1 && remainingDeposit > 0) {
      return `Deposit due from ${companyName} - ${numeral(remainingDeposit / 100).format('$0,0.00')}`;
    }
    const formattedDueAmount = numeral(currentDueInCents / 100).format('$0,0.00');

    return `Invoice due from ${companyName} - ${formattedDueAmount}`;
  }
);

export const getDefaultCustomEmailSubjectOverview = createSelector(
  [isEstimate, getCompanyName, getDescription, getSegmentOverviewDue],
  (isEstimate, companyName, description, overviewDueInCents) => {
    if (isEstimate) {
      return `Estimate from ${companyName} - ${description}`;
    }

    const formattedDueAmount = numeral(overviewDueInCents / 100).format('$0,0.00');

    return `Invoice due from ${companyName} - ${formattedDueAmount}`;
  }
);

export const getDefaultCustomEmailSubjectBatch = createSelector(
  [getOrganizationName, getSelectedTotalOutstandingBalance, getCustomerOutstandingBalanceFromSelectedJobs],
  (companyName, jobEstimateDueAmount, customerRequestsDueAmount) => {
    if (jobEstimateDueAmount !== 0) {
      return `Invoice due from ${companyName} - ${numeral(jobEstimateDueAmount).format('$0,0.00')}`;
    }
    return `Invoice due from ${companyName} - ${numeral(customerRequestsDueAmount).format('$0,0.00')}`;
  }
);

export function getCustomEmailContent(isBatch) {
  if (isBatch) {
    return getDefaultCustomEmailMessageBatch;
  }
  return getDefaultCustomEmailMessage;
}

export const getDefaultCustomEmailMessage = createSelector(
  [
    getCompanyName,
    getCustomerName,
    getParentCustomerDisplayName,
    getInvoiceNumber,
    isEstimate,
    getStartTime,
    getCompletionTime,
    getSelectedSegmentIds,
    getRemainingDeposit,
    getJobInboxFromJob,
    getPartnerName,
  ],
  (
    companyName,
    customerName,
    parentCustomerDisplayName,
    invoiceNumber,
    isEstimate,
    startTime,
    completionTime,
    selectedSegmentIds,
    remainingDeposit,
    jobInbox,
    partnerName
  ) => {
    const recipientName = isPresent(parentCustomerDisplayName) ? parentCustomerDisplayName : customerName;
    if (isEstimate) {
      return `Hi ${recipientName},\n\nAttached please find estimate #${invoiceNumber} from ${companyName}.\n\nThank you.`;
    }
    let dateString = '';
    if (completionTime) {
      dateString = ` on ${moment(completionTime).format('MMM DD, YYYY')}`;
    } else if (startTime) {
      dateString = ` on ${moment(startTime).format('MMM DD, YYYY')}`;
    }
    if (selectedSegmentIds.length <= 1 && remainingDeposit > 0) {
      return `Hi ${recipientName},\n\nAttached please find your Deposit for invoice #${invoiceNumber} for your service${dateString}.\n\nThank you.`;
    }
    if (jobInbox) {
      const jobInboxRecipientName = isPresent(parentCustomerDisplayName) ? partnerName : customerName;
      const externalId = jobInbox?.external_id;
      if (externalId) {
        return `Hi ${jobInboxRecipientName},\n\nAttached please find invoice #${invoiceNumber} for service job ID#${externalId} for ${customerName}${dateString}.\n\nThank you.`;
      }
    }
    return `Hi ${recipientName},\n\nAttached please find invoice #${invoiceNumber} for your service${dateString}.\n\nThank you.`;
  }
);

export const getDefaultCustomEmailMessageBatch = createSelector(
  [getCustomerContactInfo, getSelectedJobInvoiceNumbers, getCustomerSelectedInvoiceNumbers],
  (contactInfo, jobEstimateInvoiceNumbers, customerRequestsInvoiceNumbers) => {
    const recipientName = contactInfo.display_name;
    if (jobEstimateInvoiceNumbers.length !== 0) {
      const invoiceNumbersString = jobEstimateInvoiceNumbers.join(', #');
      return `Hi ${recipientName},\n\nAttached please find invoice #${invoiceNumbersString} for your services.\n\nThank you.`;
    }
    const invoiceNumbersString = customerRequestsInvoiceNumbers.join(', #');
    return `Hi ${recipientName},\n\nAttached please find invoice #${invoiceNumbersString} for your services.\n\nThank you.`;
  }
);

export function getCustomTextMessage(isBatch) {
  if (isBatch) {
    return getDefaultCustomTextMessageBatch;
  }
  return getDefaultCustomTextMessage;
}

export const getProposalEmailSubject = (proposalTitle) =>
  createSelector([getCompanyName], (companyName) => {
    let subject = `New proposal from ${companyName}`;

    if (isPresent(proposalTitle)) {
      subject += ` - ${proposalTitle}`;
    }

    return subject;
  });

export const getProposalEmailMessage = createSelector(
  [getCompanyName, getCustomerName, getParentCustomerDisplayName],
  (companyName, customerName, parentCustomerDisplayName) => {
    const recipientName = isPresent(parentCustomerDisplayName) ? parentCustomerDisplayName : customerName;
    return `Hi ${recipientName},\n\n${companyName} has sent you a service proposal.`;
  }
);

export const getProposalTextMessage = createSelector([getCompanyName], (companyName) => {
  return `Click to view your proposal from ${companyName}: `;
});

export function getDefaultMobileNumberForInvoice(parentCustomerNumber, customerInfoNumber) {
  if (isPresent(parentCustomerNumber)) {
    return formatPhoneNumber(parentCustomerNumber);
  }
  return formatPhoneNumber(customerInfoNumber);
}

export const getDefaultCustomTextMessage = createSelector(
  [isEstimate, getCompanyName, getSelectedSegmentIds, getRemainingDeposit],
  (isEstimate, companyName, selectedSegmentIds, remainingDeposit) => {
    if (isEstimate) {
      return `Click to view your estimate from ${companyName}: `;
    }
    if (selectedSegmentIds.length <= 1 && remainingDeposit > 0) {
      return `Deposit due from  ${companyName}`;
    }
    return `Invoice due from ${companyName}`;
  }
);

export const getDefaultCustomTextMessageBatch = createSelector([getOrganizationName], (companyName) => {
  return `Invoice due from ${companyName}`;
});

export const fetchInvoiceOptions = {
  fetchingApiFunc: InvoiceSettingsApi.show,
  selector: getInvoiceOptions,
  paramsFromProps: (props) => {
    return [props.requestId];
  },
  dataNamespace: INVOICE_OPTION_NAMESPACE,
  alwaysFetch: true,
};

export const fetchInvoiceAttachments = {
  fetchingApiFunc: RequestApi.fetchInvoiceAttachments,
  selector: getAttachmentsInfo,
  paramsFromProps: (props) => {
    return [props.requestIds, props.jobUuids];
  },
  dataNamespace: JOB_NAMESPACE,
  alwaysFetch: true,
};

export function receivedNewInvoiceOptions(data) {
  return receivedServerData(data, INVOICE_OPTION_NAMESPACE);
}

export function receivedNewInvoiceBody(data) {
  return receivedServerData(data, INVOICE_BODY_NAMESPACE);
}

export function clearInvoiceBody() {
  return clearServerData(INVOICE_BODY_NAMESPACE);
}

export const getCustomerId = createSelector([getCustomerContactInfo], (contact) => {
  return contact.id;
});

export const getInvoiceDialogUIState = createSelector(
  (state) => state[INVOICE_DIALOG_NAMESPACE],
  (dialogUiState) => {
    return dialogUiState || {};
  }
);

export const getDialogLoading = createSelector([getInvoiceDialogUIState], (dialogUiState) => {
  return dialogUiState.loading || false;
});

export const getInvoiceDialogOpenState = createSelector([getInvoiceDialogUIState], (dialogUiState) => {
  return dialogUiState.open || false;
});

export const getInvoiceDialogType = createSelector(getInvoiceDialogUIState, (dialogUiState) => {
  return dialogUiState.type;
});

export const getInvoiceIsSent = createSelector([getInvoiceDialogUIState], (dialogUiState) => {
  return dialogUiState.sent || false;
});

export const shouldAllowPaymentDialogOpen = createSelector([getInvoiceDialogUIState], (dialogUiState) => {
  return dialogUiState.shouldAllowPaymentDialogOpen || false;
});

export const getSettingsToggleLoading = createSelector([getInvoiceDialogUIState], (dialogUiState) => {
  return dialogUiState.settings_toggle_loading || false;
});

export const getUpdatedEmailMessage = (depositAmount, selectedSegmentIds) =>
  createSelector(
    [
      getParentCustomerDisplayName,
      getCustomerName,
      getInvoiceNumber,
      getCompletionTime,
      getStartTime,
      getJobInboxFromJob,
      getPartnerName,
    ],
    (parentCustomerDisplayName, customerName, invoiceNumber, completionTime, startTime, jobInbox, partnerName) => {
      const recipientName = isPresent(parentCustomerDisplayName) ? parentCustomerDisplayName : customerName;
      let dateString = '';
      if (completionTime) {
        dateString = ` on ${moment(completionTime).format('MMM DD, YYYY')}`;
      } else if (startTime) {
        dateString = ` on ${moment(startTime).format('MMM DD, YYYY')}`;
      }

      let emailMessage = '';

      if (hasDepositDue(selectedSegmentIds, depositAmount)) {
        emailMessage = `Hi ${recipientName},\n\nAttached please find your Deposit for invoice #${invoiceNumber} for your service${dateString}.\n\nThank you.`;
      } else {
        const externalId = jobInbox.external_id;
        if (externalId) {
          emailMessage = `Hi ${
            partnerName || recipientName
          },\n\nAttached please find invoice #${invoiceNumber} for service job ID#${externalId} for ${customerName}${dateString}.\n\nThank you.`;
        } else {
          emailMessage = `Hi ${recipientName},\n\nAttached please find invoice #${invoiceNumber} for your service${dateString}.\n\nThank you.`;
        }
      }
      return emailMessage;
    }
  );

export function hasDepositDue(selectedSegmentIds, depositAmount) {
  if (isPresent(selectedSegmentIds) && selectedSegmentIds.length == 1 && depositAmount > 0) {
    return true;
  }
  return false;
}

export const showInvoiceOrEstimateUrl = (requestId, selectedSegmentIds) => {
  let show_invoice_or_estimate_url = `/pro/jobs/${requestId}/show_invoice_or_estimate?`;
  selectedSegmentIds.forEach((segmentId) => {
    show_invoice_or_estimate_url += `request_ids[]=${segmentId}&`;
  });
  return show_invoice_or_estimate_url;
};

export const getUpdatedRemainingDeposit = (selectedSegmentIds) =>
  createSelector([getSegments], (segmentArray) => {
    let remainingDeposit = 0;
    if (isPresent(selectedSegmentIds) && selectedSegmentIds.length == 1) {
      let segmentSelected = segmentArray.segments.find(function (element) {
        return element.id == selectedSegmentIds[0];
      });
      remainingDeposit = segmentSelected.payment_information.remaining_deposit;
    }
    return remainingDeposit;
  });

export const isValidDate = (date) => {
  return moment(date, 'YYYY-MM-DD', true).isValid();
};

export const getTextNotification = createSelector(
  (state) => state.textNotificationReducers,
  (notifications) => {
    return notifications || {};
  }
);

export const getSmsNumber = createSelector(getTextNotification, (notifications) => {
  return notifications.smsNumber || '';
});
